import axios from 'axios';
import { useAuthStore } from '../store';

const createApiClient = () => {
    const ret = axios.create({
        baseURL: '',
    });

    ret.interceptors.response.use(
        (response) => response,
        (error) => {
            const statusCode = error?.response?.status;
            if (statusCode === 401 || statusCode === 403) {
                // handle 401 error here
                useAuthStore.setState({
                    authToken: null,
                    userProfile: null,
                });
                return Promise.reject(new Error('Unauthorized'));
            }
            return Promise.reject(error);
        }
    );
    return ret;
};

const api = createApiClient();
export const login = async (model) =>
    await request(api.post('/api/v1.0/auth/login', model));

export const signup = async (model) =>
    await request(api.post('/api/v1.0/auth/create', model));

export const forgotPassword = async (model) =>
    await request(api.post('/api/v1.0/auth/forgot', model));

export const resetPassword = async (model) =>
    await request(api.post('/api/v1.0/auth/reset', model));

export const resendEmail = async () =>
    await request(api.get('/api/v1.0/auth/resend'));

export const getUserProfile = async () =>
    await request(api.get('/api/v1.0/auth/profile'));

export const updateEmail = async (model) =>
    await request(api.post('/api/v1.0/auth/email', model));

export const updatePassword = async (model) =>
    await request(api.post('/api/v1.0/auth/password', model));

export const getSecurityHistory = async (pagination) =>
    await request(
        api.get('/api/v1.0/security/history', { params: pagination })
    );

export const getCampaigns = async ({ types, userId, pagination }) =>
    await request(
        api.get('/api/v1.0/campaigns', {
            params: {
                ...pagination,
                types: types,
                userId: userId,
            },
        })
    );

export const getCampaignById = async (id) =>
    await request(api.get(`/api/v1.0/campaigns/${id}`));

export const addOrUpdateCampaign = async (model) =>
    await request(api.post('/api/v1.0/campaigns', model));

export const campaignAction = async ({ id, model }) =>
    await request(api.post(`/api/v1.0/campaigns/${id}/action`, model));

export const replaceCampaignShortLink = async (model) =>
    await request(api.post(`/api/v1.0/campaigns/shortlinks/replace`, model));

export const getCampaignTimeseries = async ({ id, ...rest }) =>
    await request(
        api.get(`/api/v1.0/campaigns/${id}/series`, {
            params: {
                ...rest,
            },
        })
    );

export const getCampaignEvents = async ({ id, pagination }) =>
    await request(
        api.get(`/api/v1.0/campaigns/${id}/events`, { params: pagination })
    );

export const getAvailableAgents = async ({ routeId, campaignId, pagination }) =>
    await request(
        api.get(`/api/v1.0/agents/available`, {
            params: {
                ...pagination,
                campaignId,
                routeId,
            },
        })
    );

export const getDatasets = async (pagination) =>
    await request(api.get('/api/v1.0/data/sets', { params: pagination }));

export const getUserDatasets = async ({ userId, type, pagination }) =>
    await request(
        api.get(`/api/v1.0/data/sets`, {
            params: {
                ...pagination,
                userId,
            },
        })
    );

export const getDatasetById = async (id) =>
    await request(api.get(`/api/v1.0/data/sets/${id}`));

export const addDataset = async (model) =>
    await request(api.post('/api/v1.0/data/sets', model));

export const updateDataSetFiles = async ({ dataSetId, ...model }) =>
    await request(api.post(`/api/v1.0/data/sets/${dataSetId}/files`, model));

export const refreshDataSet = async ({ id, ...model }) =>
    await request(api.post(`/api/v1.0/data/sets/${id}/refresh`, model));

export const getDatafiles = async (pagination) =>
    await request(
        api.get(`/api/v1.0/data/files`, {
            params: pagination,
        })
    );

export const getDataFileById = async (id) =>
    await request(api.get(`/api/v1.0/data/files/${id}`));

export const refreshDataFile = async ({ id, ...model }) =>
    await request(api.post(`/api/v1.0/data/files/${id}/refresh`, model));

export const importDataRecords = async (formData) =>
    await request(
        api.post('/api/v1.0/data/files', formData, {
            headers: {
                'Content-Type': `multipart/form-data; boundary=${Math.random().toString()}`,
            },
        })
    );

export const getVerticals = async (pagination) =>
    await request(
        api.get(`/api/v1.0/campaigns/verticals`, {
            params: pagination,
        })
    );

export const getUserVerticals = async ({ userId, pagination }) =>
    await request(
        api.get(`/api/v1.0/campaigns/verticals`, {
            params: {
                ...pagination,
                userId,
            },
        })
    );

export const getUserRoutes = async ({ userId, type = null, pagination }) =>
    await request(
        api.get(`/api/v1.0/users/routes/${type || ''}`, {
            params: {
                ...pagination,
                type,
                userId,
            },
        })
    );

export const getAllRoutes = async (pagination) =>
    await request(
        api.get(`/api/v1.0/routes`, {
            params: pagination,
        })
    );

export const getRouteById = async (id) =>
    await request(api.get(`/api/v1.0/routes/${id}`));

export const getUserRoutesByRouteId = async ({ routeId, pagination }) =>
    await request(
        api.get(`/api/v1.0/routes/${routeId}/users`, {
            params: pagination,
        })
    );

export const getUserApiKeys = async ({ userId, pagination }) =>
    await request(
        api.get(`/api/v1.0/users/keys`, {
            params: {
                ...pagination,
                userId,
            },
        })
    );

export const getUserBillings = async ({ userId, pagination }) =>
    await request(
        api.get(`/api/v1.0/billing`, {
            params: {
                ...pagination,
                userId,
            },
        })
    );

export const getChatHandlers = async (pagination) =>
    await request(
        api.get('/api/v1.0/campaigns/handlers', { params: pagination })
    );

export const getUserChatHandlers = async ({ userId, pagination }) =>
    await request(
        api.get('/api/v1.0/campaigns/handlers', {
            params: {
                ...pagination,
                userId: userId,
            },
        })
    );
export const getChatHandlerById = async (id) =>
    await request(api.get(`/api/v1.0/campaigns/handlers/${id}`));

export const addOrUpdateChatHandler = async (model) =>
    await request(api.post(`/api/v1.0/campaigns/handlers`, model));

export const getChatScripts = async (pagination) =>
    await request(
        api.get('/api/v1.0/campaigns/scripts', { params: pagination })
    );

export const getChatScriptById = async (id) =>
    await request(api.get(`/api/v1.0/campaigns/scripts/${id}`));

export const addOrUpdateChatScript = async (model) =>
    await request(api.post(`/api/v1.0/campaigns/scripts`, model));

export const getChatBots = async (pagination) =>
    await request(api.get('/api/v1.0/campaigns/bots', { params: pagination }));

export const getChatBotsById = async (id) =>
    await request(api.get(`/api/v1.0/campaigns/bots/${id}`));

export const addOrUpdateChatBot = async (model) =>
    await request(api.post(`/api/v1.0/campaigns/bots`, model));

export const getConversations = async (pagination) =>
    await request(
        api.get('/api/v1.0/engagement/sms/conversations', {
            params: pagination,
        })
    );

export const getConversationById = async (id) =>
    await request(api.get(`/api/v1.0/engagement/sms/conversations/${id}`));

export const getConversationMessages = async ({ id, pagination }) =>
    await request(
        api.get(`/api/v1.0/engagement/sms/conversations/${id}/messages`, {
            params: pagination,
        })
    );

export const getVoiceCalls = async (pagination) =>
    await request(
        api.get('/api/v1.0/engagement/calls', { params: pagination })
    );

export const getVoiceCallById = async (id) =>
    await request(api.get(`/api/v1.0/engagement/calls/${id}`));

export const getMessages = async (pagination) =>
    await request(
        api.get('/api/v1.0/engagement/sms/messages', { params: pagination })
    );

export const getMessageGroups = async ({ type = null, pagination }) =>
    await request(
        api.get(`/api/v1.0/messages/groups/${type || ''}`, {
            params: pagination,
        })
    );

export const getUserMessageGroups = async ({
    userId,
    type = null,
    pagination,
}) =>
    await request(
        api.get(`/api/v1.0/messages/groups/${type || ''}`, {
            params: {
                userId,
                ...pagination,
            },
        })
    );

export const getMessageGroupById = async (id) =>
    await request(api.get(`/api/v1.0/messages/groups/${id}`));

export const addOrUpdateMessageGroup = async (id = null, model) =>
    await request(api.post(`/api/v1.0/messages/groups/${id || ''}`, model));

export const addMessage = async (model) =>
    await request(api.post(`/api/v1.0/messages`, model));

export const getMediaGroups = async (pagination) =>
    await request(
        api.get(`/api/v1.0/media/groups`, {
            params: pagination,
        })
    );

export const getUserMediaGroups = async ({ userId, pagination }) =>
    await request(
        api.get(`/api/v1.0/media/groups`, {
            params: {
                userId,
                ...pagination,
            },
        })
    );

export const getMediaGroupById = async (id) =>
    await request(api.get(`/api/v1.0/media/groups/${id}`));

export const addOrUpdateMediaGroup = async (id = null, model) =>
    await request(api.post(`/api/v1.0/media/groups/${id || ''}`, model));

export const getLinkGroups = async ({ userId, pagination }) =>
    await request(
        api.get(`/api/v1.0/links/groups`, {
            params: {
                userId,
                ...pagination,
            },
        })
    );

export const getUserLinkGroups = async ({ userId, pagination }) =>
    await request(
        api.get(`/api/v1.0/links/groups`, {
            params: {
                userId,
                ...pagination,
            },
        })
    );

export const getLinkGroupById = async (id) =>
    await request(api.get(`/api/v1.0/links/groups/${id}`));

export const addOrUpdateLinkGroup = async (id = null, model) =>
    await request(api.post(`/api/v1.0/links/groups/${id || ''}`, model));

export const addLink = async (model) =>
    await request(api.post(`/api/v1.0/links`, model));

export const addMedia = async (formData) =>
    await request(
        api.post(`/api/v1.0/media`, formData, {
            headers: {
                'Content-Type': `multipart/form-data; boundary=${Math.random().toString()}`,
            },
        })
    );

export const toggleGroupProperty = async ({ type, id, model }) =>
    await request(api.post(`/api/v1.0/${type}/${id}/toggle`, model));

export const toggleUserProperty = async (model) =>
    await request(api.post(`/api/v1.0/users/toggle`, model));

export const addUserBalance = async (model) =>
    await request(api.post(`/api/v1.0/users/balance`, model));

export const getAgents = async (pagination) =>
    await request(
        api.get(`/api/v1.0/agents`, {
            params: pagination,
        })
    );

export const getAgentEvents = async ({ id, pagination }) =>
    await request(
        api.get(`/api/v1.0/agents/${id}/events`, { params: pagination })
    );

export const getAgentById = async (id) =>
    await request(api.get(`/api/v1.0/agents/${id}`));

export const getSimcards = async (pagination) =>
    await request(
        api.get(`/api/v1.0/simcards`, {
            params: pagination,
        })
    );

export const getSimcardEvents = async ({ id, pagination }) =>
    await request(
        api.get(`/api/v1.0/simcards/${id}/events`, { params: pagination })
    );

export const getSimcardById = async (id) =>
    await request(api.get(`/api/v1.0/simcards/${id}`));

export const getMobileDevices = async (pagination) =>
    await request(
        api.get(`/api/v1.0/devices`, {
            params: pagination,
        })
    );

export const getMobileDeviceById = async (id) =>
    await request(api.get(`/api/v1.0/devices/${id}`));

export const getShortlinks = async (pagination) =>
    await request(
        api.get(`/api/v1.0/shortlinks`, {
            params: pagination,
        })
    );

export const getShortlinkEvents = async ({ id, pagination }) =>
    await request(
        api.get(`/api/v1.0/shortlinks/${id}/events`, { params: pagination })
    );

export const replaceShortLink = async (model) =>
    await request(api.post(`/api/v1.0/shortlinks/replace`, model));

export const getTimeSeries = async ({ id, resource, ...rest }) =>
    await request(
        api.get(`/api/v1.0/${resource}/${id}/series`, {
            params: {
                ...rest,
            },
        })
    );

export const getShortlinkById = async (id) =>
    await request(api.get(`/api/v1.0/shortlinks/${id}`));

export const addShortlink = async (model) =>
    await request(api.post(`/api/v1.0/shortlinks`, model));

export const addSimCard = async (model) =>
    await request(api.post(`/api/v1.0/simcards`, model));

export const addSimCardBatch = async (model) =>
    await request(api.post(`/api/v1.0/simcards/batch`, model));

export const getSessions = async (pagination) =>
    await request(api.get('/api/v1.0/sessions', { params: pagination }));

export const getCampaignSessions = async ({ campaignId, pagination }) =>
    await request(
        api.get(`/api/v1.0/campaigns/${campaignId}/sessions`, {
            params: pagination,
        })
    );

export const getSimCardSessions = async ({ simcardId, pagination }) =>
    await request(
        api.get(`/api/v1.0/simcards/${simcardId}/sessions`, {
            params: pagination,
        })
    );

export const getAgentSessions = async ({ agentId, pagination }) =>
    await request(
        api.get(`/api/v1.0/agents/${agentId}/sessions`, {
            params: pagination,
        })
    );

export const getCarrierConfigs = async (pagination) =>
    await request(
        api.get(`/api/v1.0/carriers`, {
            params: pagination,
        })
    );

export const getCarrierConfigById = async (id) =>
    await request(api.get(`/api/v1.0/carriers/${id}`));

export const getApns = async (pagination) =>
    await request(
        api.get(`/api/v1.0/apns`, {
            params: pagination,
        })
    );

export const getApnById = async (id) =>
    await request(api.get(`/api/v1.0/apns/${id}`));

export const getUsers = async (pagination) =>
    await request(
        api.get(`/api/v1.0/users`, {
            params: pagination,
        })
    );

export const getUserById = async (id) =>
    await request(api.get(`/api/v1.0/users/${id}`));

export const getSystemEvents = async (pagination) =>
    await request(
        api.get(`/api/v1.0/system/events`, {
            params: pagination,
        })
    );

export const sendTestMessage = async (model) =>
    await request(api.post(`/api/v1.0/tools/test/message`, model));

/*export const getDatafiles = async ({ datasetId, pagination }) =>
    await request(
        api.get(`/api/v1.0/data/sets/${datasetId}/files`, { params: pagination })
    );*/

const request = async (axiosPromise) => {
    try {
        const response = await axiosPromise;
        const data = response.data;
        if (!data.success) {
            throw new Error(data.errors[0]);
        }
        if (data.pagination) {
            return {
                data: data.data,
                pagination: data.pagination,
            };
        }
        return data.data;
    } catch (error) {
        //console.error(error.response?.data?.message);
        throw new Error(error.message || 'An unknown error has occured');
    }
};
